import { ContentReviewInterface } from '../../models/ContentReviewInterface';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { CommentOverlay, ReviewContentType } from './reviewContentEditViewTypes';

/** Block types titled by their own heading, with the title to use when it is blank. */
const HEADING_TITLE_FALLBACKS: Record<string, string> = {
  StandardHeaderText: 'Text Section',
  Subtitle: 'Subtitle',
  LeftRightList: 'List Section',
};

/** Block types with a fixed title. */
const FIXED_SECTION_TITLES: Record<string, string> = {
  Text: 'Text',
  HeroImage: 'Hero Image',
  ImageCarousel: 'Image Carousel',
  Table: 'Table',
};

export function getSectionTitle(item: {
  type: string;
  properties?: Record<string, unknown>;
}): string {
  const fallback = HEADING_TITLE_FALLBACKS[item.type];
  if (fallback) {
    return (item.properties?.heading as { content?: string } | undefined)?.content || fallback;
  }
  return FIXED_SECTION_TITLES[item.type] ?? `${item.type} Section`;
}

export function getContentTypeDisplayName(type: string): string {
  switch (type) {
    case 'StandardHeaderText':
      return 'Header Text';
    case 'Subtitle':
      return 'Subtitle';
    case 'Text':
      return 'Text';
    case 'HeroImage':
      return 'Hero Image';
    case 'ImageCarousel':
      return 'Image Cards';
    case 'LeftRightList':
      return 'List';
    case 'Table':
      return 'Table';
    default:
      return type;
  }
}

export function getContentTypePickerLabel(type: ReviewContentType): string {
  switch (type) {
    case 'StandardHeaderText':
      return 'Standard Text';
    case 'ImageCarousel':
      return 'Image Cards';
    case 'LeftRightList':
      return 'List';
    case 'Table':
      return 'Table';
    default:
      return type;
  }
}

export function buildCommentOverlays(
  comments: ContentReviewInterface['comments'],
): CommentOverlay[] {
  return comments.map((conversation) => {
    const sectionIndex = parseInt(conversation.sectionId.replace('section-', ''), 10);
    return {
      conversation,
      sectionId: conversation.sectionId,
      top: sectionIndex * 200 + 50,
      right: 20,
    };
  });
}

export function computeHasUnsavedChanges(
  reviewData: ContentReviewInterface,
  editingSectionId: string | null,
  editedContent: unknown,
): boolean {
  const hasEditingSections = editingSectionId !== null;
  const hasUnsavedEdits = editedContent !== null;

  const hasUnsavedAdditions = reviewData.changes.some(
    (change) =>
      change.changed &&
      !change.contentBefore &&
      (change.accepted === false || change.accepted === undefined),
  );

  const hasUnsavedReplacements = reviewData.changes.some(
    (change) =>
      change.changed &&
      change.contentBefore &&
      (change.accepted === false || change.accepted === undefined),
  );

  const hasAnyUnsavedChanges = reviewData.changes.some(
    (change) => change.changed && (change.accepted === false || change.accepted === undefined),
  );

  return (
    hasEditingSections ||
    hasUnsavedEdits ||
    hasUnsavedAdditions ||
    hasUnsavedReplacements ||
    hasAnyUnsavedChanges
  );
}

export function mergeDraftSectionsWithAcceptedAdditions(
  draftContent: ContentManagementInterface,
  reviewData: ContentReviewInterface,
): unknown[] {
  const allSections = [...draftContent.content];

  reviewData.changes.forEach((change) => {
    if (change.changed && !change.contentBefore && change.accepted) {
      const sectionIndex = parseInt(change.sectionId.replace('section-', ''), 10);
      if (sectionIndex >= allSections.length) {
        allSections.push(change.contentAfter);
      } else {
        allSections.splice(sectionIndex, 0, change.contentAfter);
      }
    }
  });

  return allSections;
}

export function createAddContentItem(contentType: ReviewContentType): Record<string, unknown> {
  const timestamp = Date.now();

  switch (contentType) {
    case 'StandardHeaderText':
      return {
        id: `content-${timestamp}`,
        type: 'StandardHeaderText',
        properties: {
          heading: { id: `heading-${timestamp}`, content: 'New Section' },
          text: { id: `text-${timestamp}`, content: 'Enter your content here...' },
        },
      };
    case 'LeftRightList':
      return {
        id: `content-${timestamp}`,
        type: 'LeftRightList',
        properties: {
          items: [
            {
              heading: { id: `heading-${timestamp}`, content: 'New Item' },
              text: { id: `text-${timestamp}`, content: 'Enter your content here...' },
            },
          ],
        },
      };
    case 'Table':
      return {
        id: `content-${timestamp}`,
        type: 'Table',
        properties: {
          title: 'New Table',
          headers: ['Column 1', 'Column 2'],
          rows: [['Row 1, Col 1', 'Row 1, Col 2']],
        },
      };
    case 'ImageCarousel':
      return {
        id: `content-${timestamp}`,
        type: 'ImageCarousel',
        properties: {
          title: 'New Image Carousel',
          images: [],
        },
      };
    default:
      throw new Error(`Unsupported content type: ${contentType}`);
  }
}
